import './MessageBox.styles.css';

import { Avatar } from 'primereact/avatar';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { getPokerRoom } from '../../../constants';
import { ChatMessage } from '../../../model/chat/ChatMessage';
import { PokerRoom } from '../../../model/enumeration/PokerRoom';
import notificationStore from '../../../store/NotificationStore';
import translateStore from '../../../store/TranslateStore';
import userSettingsStore from '../../../store/UserSettingsStore';
import { formatInstantTo24HourTime } from '../../../utils/date.utils';
import { isUnicodeEmoji } from '../../../utils/text.utils';
import CountryFlag from '../../CountryFlag/CountryFlag';
import { MessageBoxProps } from './MessageBoxProps';

export const MessageBox: FC<MessageBoxProps> = ({
  message,
  style,
  hidePid,
  messageId,
  onClick,
  isNotification,
}) => {
  const [isMessageTranslated, setMessageTranslated] = useState<boolean>(false);
  const [translatedMessage, setTranslatedMessage] = useState<string>('');
  const [isTranslationLoading, setTranslationLoading] =
    useState<boolean>(false);

  const formatDate = useCallback(
    (dateString: string) => formatInstantTo24HourTime(dateString),
    [],
  );

  const emojiFolderAndSize = useMemo(() => {
    const pokerApp: PokerRoom = getPokerRoom();
    switch (pokerApp) {
      case PokerRoom.ClubGG: {
        return ['emoji_clubgg', 40];
      }
      case PokerRoom.PokerMan: {
        return ['emoji_pokerman', 60];
      }
      default:
        return [null, null];
    }
  }, []);

  const getChatMessageContent = (chatMessage: ChatMessage): React.ReactNode => {
    if (isUnicodeEmoji(chatMessage.text)) {
      return <span className="regular-emoji">{chatMessage.text}</span>;
    } else if (chatMessage.emoji) {
      const [folder, size] = emojiFolderAndSize;
      if (folder === null || size === null) {
        return <p className="not-supported-emoji">#NOT SUPPORTED EMOJI#</p>;
      }
      return (
        <img
          src={`/assets/${folder}/${chatMessage.text}.png`}
          alt={`emoji_${chatMessage.text}`}
          width={size}
          height={size}
        />
      );
    } else {
      return isMessageTranslated ? translatedMessage : chatMessage.text;
    }
  };

  const content = useMemo((): React.ReactNode => {
    if ('text' in message) {
      return <div className="text">{getChatMessageContent(message)}</div>;
    } else if ('errorMessage' in message) {
      return <div className="text">{message.errorMessage}</div>;
    }
  }, [message, isMessageTranslated]);

  const isNotEmoji =
    'text' in message && !isUnicodeEmoji(message.text) && !message.emoji;

  const onTranslate = (
    event: React.MouseEvent<HTMLDivElement>,
    text: string,
  ): void => {
    event.stopPropagation();

    if (isTranslationLoading) {
      return;
    }

    if (isMessageTranslated) {
      setMessageTranslated(false);
      setTranslatedMessage('');
      return;
    }

    const translatedText = translateStore.getTranslation({
      text,
      language: userSettingsStore.settings.chat.language,
    });

    if (translatedText !== undefined) {
      setTranslatedMessage(translatedText);
      setMessageTranslated(true);
      return;
    }

    setTranslationLoading(true);
    translateStore
      .translate(text, userSettingsStore.settings.chat.language)
      .then(text => {
        setTranslatedMessage(text);
        setMessageTranslated(true);
      })
      .catch(() =>
        notificationStore.showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Translation error. Please try again',
        }),
      )
      .finally(() => setTranslationLoading(false));
  };

  return (
    <div
      id={messageId && `message-${messageId}`}
      key={message.id}
      className="message mb-3 p-1"
      style={style}
      onClick={() => onClick?.(message)}
    >
      <div className="p-0 text-center player-info">
        <Avatar
          shape="circle"
          size="large"
          image={message.myBot ? '/assets/bot.svg' : '/assets/player.svg'}
          children={
            <div className="chat-country-flag">
              <CountryFlag countryCode={message.country} />
            </div>
          }
        />
        <div>{message.nick}</div>
        {!hidePid && <div>{message.pid}</div>}
      </div>
      <div className="align-self-center msg-part">{content}</div>
      <div className="msg-part msg-time">
        {isNotEmoji && !isNotification && (
          <div
            className="translate"
            onClick={e => onTranslate(e, message.text)}
          >
            {isMessageTranslated ? (
              'show original'
            ) : (
              <div className="d-flex gap-1">
                <i className="pi pi-language" />
                <p className="m-0">translate</p>
              </div>
            )}
          </div>
        )}
        <div>{formatDate(message.eventTime)}</div>
      </div>
    </div>
  );
};
